import { WorkareaPricingData } from "../workareas/workareaPricing"
import { ItemClass, PricingType, ProjectStatus, ProjectType } from "../../common/constants"
import { GpsLog } from "../../components/maps/GpsLog"

export type Visit = {
  id: string
  date: string
  billed: boolean
  tasks: string
  technicians: string
  laborTime: number
  time: number
  status: number
  clientNotes: string
  internalNotes: string
  timesheets: Timesheet[]
  owner: string
  taskIds: number[]
  hasPhotos: boolean
}

export const defaultVisit = ():Visit => ({
  id: '',
  date: '',
  billed: false,
  tasks: '',
  technicians: '',
  laborTime: 0,
  time: 0,
  status: 0,
  clientNotes: '',
  internalNotes: '',
  timesheets: [],
  owner: '',
  taskIds: [],
  hasPhotos: false
})

export type Timesheet = {
  id: string
  crewMemberId: number
  workareaId?: number
  taskId?: number | null
  time?: number | null
  startedAt: string
  endedAt?: string
  lunch?: number
  gpsLog: GpsLog[]
  canBeEdited: boolean
}

export enum VisitReviewedState {
  NotReviewed = 1,
  Reviewed = 2
}

export type TimesheetGpsData = {
  timesheetId: string
  center: GpsPosition
  clockIn: GpsPosition
  clockOut: GpsPosition
}

export type GpsPosition = {
  latitude: number
  longitude: number
}

export type UpdateVisitRequest = {
  visitId: string
  billed: boolean
  clientNotes: string
  internalNotes: string
  status: number
}

export type LegacyCustomerEmail = {
  id: number
  address: string
  description?: string | null
  isPrimary: boolean
}

export type LegacyCustomerPhone = {
  id: number
  type: number
  number: string
  description?: string | null
  isPrimary: boolean
}

export type LegacyCustomerProperty = {
  id: number
  address1: string
  mapPosition?: string | null
}

export type LegacyJobCustomer = {
  id: number
  url: string
  displayName: string
  address1?: string | null
  address2?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
  isActive: boolean
  qboId?: string
  qboDisplayName?: string
  emails: LegacyCustomerEmail[]
  phones: LegacyCustomerPhone[]
  /** Not populated by the job endpoint — only the customers endpoint fills these in. */
  properties?: number
  propertyList?: LegacyCustomerProperty[]
}

export type LegacyJobProperty = {
  id: number
  url: string
  displayName: string
  address1?: string | null
  address2?: string | null
  county?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
  notes?: string | null
  mapPosition?: string | null
}

export type LegacyScheduleDate = {
  id: number
  projectWorkareaId?: number | null
  projectWorkareaName?: string | null
  starting: string
  ending: string
  assignedTo?: string | null
  assignedToAvatar?: string | null
  notes?: string | null
  workingDays?: number | null
}

export type LegacyJobData = {
  id: number
  url: string
  type: ProjectType
  no: string
  name: string
  estimateDate: string
  soldDate?: string | null
  completedDate?: string | null
  customerId: number
  propertyId: number
  customer: LegacyJobCustomer
  property: LegacyJobProperty
  sold: boolean
  lost: boolean
  active: boolean
  archivedAtUtc?: string | null
  archivedBy?: string | null
  status?: number | null
  divisionId?: number | null
  consultId?: number | null
  notifications: boolean
  salesPersonEmail?: string | null
  salesPersonName?: string | null
  estimatorEmail?: string | null
  schedulers?: string | null
  schedulingNotes?: string | null
  crewNotes?: string | null
  clientNotes?: string | null
  timecardInternalNotesInputMode: number
  timecardClientNotesInputMode: number
  primaryPhotoUrl?: string | null
  attachments: number
  defaultAttachmentFolder: string
  scheduleDates: LegacyScheduleDate[]
  workareas: LegacyWorkareaData[]
  receipts: LegacyReceiptData[]
  budgets: LegacyBudgetData[]
  budgetYear: number
}

export type LegacyReceiptData = {
  id: number
  projectId: number
  vendorId: number
  vendorName: string
  receiptDate: string
  receiptDateUtc?: string
  receiptNumber: string
  customTaxRate?: number | null
  customTotal?: number | null
  notes?: string | null
  owner: string
  removed?: boolean
}

/**
 * Mirrors UpdateJobDto in the api repo (SynkedUp.Domain/Jobs/UpdateJobDto.cs).
 * Every field here is written unconditionally by JobFieldsUpdater, so omitting
 * one clears it on the server.
 */
export type UpdateJobRequest = {
  id: number
  type: ProjectType
  no: string
  name: string
  estimateDate: string
  soldDate?: string | null
  completedDate?: string | null
  customerId: number
  propertyId: number
  budgetYear: number
  divisionId?: number | null
  consultId?: number | null
  salesPersonEmail?: string | null
  estimatorEmail?: string | null
  schedulers?: string | null
  schedulingNotes?: string | null
  sold: boolean
  lost: boolean
  active: boolean
  status?: number | null
  notifications: boolean
  clientNotes?: string | null
  crewNotes?: string | null
  timecardInternalNotesInputMode: number
  timecardClientNotesInputMode: number
  workareas: LegacyWorkareaData[]
  visits: unknown[]
  receipts: unknown[]
  itemsChangeSet: UpdateJobItemsChangeSet | null
}

export type UpdateJobItemsChangeSet = {
  addedEstimatedItems: unknown[]
  addedActualItems: unknown[]
  updatedEstimatedItems: unknown[]
  updatedActualItems: unknown[]
  deletedItems: string[]
}

export type LegacyWorkareaData = {
  recovery?: number
  workSubtotal?: number | null
  pricing?: WorkareaPricingData | null
  id: number
    name: string
    pricingType: PricingType
    isApproved: boolean
    isOnHold: boolean
    master?: boolean
    masterWorkareaId?: number | null
    costCodeId: number
    costCodeQboId?: string | null
    divisionId: number
    scheduleDateUtc?: string
    quotedPrice?: number
    customQuotedPrice?: number
    notes?: string
    customerAndInvoiceNotes?: string
    invoiceDescription?: string | null
    sortIndex: number
    workareaGroupId?: string
    status?: ProjectStatus
    estimatedOccurrences?: number
    customLaborRateItemId?: number | null
    showTaxInFinalPrice?: boolean
    completedDate?: string | null
    recurringScheduleConfig?: string
    scheduleDate?: string | null
    hours?: number
    alreadyInvoiced?: number
    removed?: boolean
}

type LegacyBudgetData = {
  year: number
  class: ItemClass
  tenantId: number
  overhead: number
  profit: number
  taxRate: number
}

export const JOB_DETAILS_CLEANUP_FEATURE = 'job-details-cleanup'
